import React, { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { captureException } from "@/config/sentry";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  eventId: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to console
    console.error("Error Boundary caught an error:", error, errorInfo);

    // Send to Sentry with additional context
    const eventId = Sentry.withScope((scope) => {
      scope.setContext("errorBoundary", {
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
        errorBoundaryProps: this.props,
      });
      scope.setLevel("error");
      return Sentry.captureException(error);
    });

    this.setState({
      error,
      errorInfo,
      eventId,
    });
  }

  handleReset = () => {
    if (this.props.onReset) {
      this.props.onReset();
    }
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gray-50 py-8 flex items-center justify-center">
          <div className="container mx-auto px-4 max-w-2xl">
            <Card className="border-red-200">
              <CardHeader className="text-center">
                <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <CardTitle className="text-2xl font-bold text-red-900">
                  Something went wrong
                </CardTitle>
                <CardDescription className="text-lg text-red-700">
                  We've encountered an unexpected error. Our team has been
                  notified.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-900 mb-2">
                    Error Details:
                  </h4>
                  <p className="text-sm text-red-700 font-mono">
                    {this.state.error?.message || "Unknown error occurred"}
                  </p>
                  {this.state.eventId && (
                    <p className="text-xs text-gray-600 mt-2">
                      Error ID: {this.state.eventId}
                    </p>
                  )}
                </div>

                <div className="text-sm text-gray-600">
                  <p className="mb-2">You can try the following:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Click "Try Again" to reset this section</li>
                    <li>Refresh the page to start over</li>
                    <li>Go back to the homepage</li>
                    <li>Contact support if the problem persists</li>
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button onClick={this.handleReset} className="flex-1">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button
                    onClick={this.handleReload}
                    variant="outline"
                    className="flex-1"
                  >
                    Refresh Page
                  </Button>
                  <Button
                    onClick={this.handleGoHome}
                    variant="outline"
                    className="flex-1"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Go Home
                  </Button>
                </div>

                {import.meta.env.DEV && this.state.errorInfo && (
                  <details className="mt-6">
                    <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                      Developer Details (Development Only)
                    </summary>
                    <div className="mt-2 p-4 bg-gray-100 rounded-lg">
                      <pre className="text-xs text-gray-800 whitespace-pre-wrap overflow-auto">
                        {this.state.error?.stack}
                        {"\n\nComponent Stack:"}
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </div>
                  </details>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Export Sentry's error boundary as well for specific use cases
export const SentryErrorBoundary = Sentry.ErrorBoundary;
